import {
	deleteDraftIP,
	getActiveMips,
} from '@dealstack/frontend/shared/dataExchange/services/god.service';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import { useDSToast } from '@dealstack/frontend/shared/mechanism/toast/useDSToast';
import { DataSimpleMipTct } from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useDebounceValue } from '@dealstack/frontend/shared/other/react/hooks/useDebounceValue';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	getCoreRowModel,
	getSortedRowModel,
	SortingState,
	useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { DATE_FORMATS, formatDateToUI } from '@dealstack/shared/lib/format/dates/date';
import { Button } from '@mantine/core';
import { Link } from 'react-router-dom';
import { GODMODE_ROOT_ROUTES } from '@dealstack/frontend/admin/pages/Routing/Routing.tsx';

const DRAFT_STATE = 'DRAFT';

export enum MANAGE_MIPS_FORM_FIELDS {
	SEARCH_QUERY = 'search_query',
}
const EMPTY_DATA: Array<DataSimpleMipTct> = [];

export enum OPTIONS {
	FALSE = 'false',
	TRUE = 'true',
}

export const deleteDraftModalDefaultValues = {
	visible: false,
	mipId: null,
};

export const useListMip = () => {
	const [sorting, setSorting] = useState<SortingState>([]);
	const toastAPI = useDSToast();
	const [showArchived, setShowArchived] = useState<OPTIONS>(OPTIONS.FALSE);
	const [showDraft, setShowDraft] = useState<OPTIONS>(OPTIONS.TRUE);

	const [deleteDraftMipConfirmationModal, setDeleteDraftMipConfirmationModal] = useState(
		deleteDraftModalDefaultValues
	);

	const manageSearchQueryForm = useForm({
		mode: 'all',
		reValidateMode: 'onChange',
	});

	const stringInputValue = useWatch({
		control: manageSearchQueryForm.control,
		name: MANAGE_MIPS_FORM_FIELDS.SEARCH_QUERY,
	});

	const searchQuery = useDebounceValue(stringInputValue, 1000);

	const manageSelectForm = useForm({
		reValidateMode: 'onChange',
		defaultValues: {
			showArchived,
			showDraft,
		},
	});

	useEffect(() => {
		setShowArchived(manageSelectForm.watch('showArchived'));
	}, [manageSelectForm.watch('showArchived')]);

	useEffect(() => {
		setShowDraft(manageSelectForm.watch('showDraft'));
	}, [manageSelectForm.watch('showDraft')]);

	const getMIPRequest = useQuery({
		queryKey: [
			API_PATHS.GODMODE_MIPS_ACTIVE_POST,
			{
				searchQuery,
				includeDeleted: showArchived === OPTIONS.TRUE,
				includeDraft: showDraft === OPTIONS.TRUE,
			},
		],

		queryFn: () =>
			getActiveMips(API_PATHS.GODMODE_MIPS_ACTIVE_POST, {
				searchQuery,
				includeDeleted: showArchived === OPTIONS.TRUE,
				includeDraft: showDraft === OPTIONS.TRUE,
			}),
		meta: { errorToast: 'Data cannot be shown!' },
	});

	const deleteDraftIPRequest = useMutation({
		mutationFn: deleteDraftIP,
	});
	const deleteDraftIPOnSubmit = (mipId: string | null, withEntityDelete?: boolean) => {
		if (!mipId) {
			return;
		}
		deleteDraftIPRequest.mutate(
			{ mipId, withEntityDelete },
			{
				onSuccess: () => {
					toastAPI.success({
						message: 'Draft Investment Programme is successfully deleted!',
					});
					setDeleteDraftMipConfirmationModal(deleteDraftModalDefaultValues);
					// oxlint-disable-next-line typescript/no-floating-promises
					getMIPRequest.refetch();
				},
				onError: (error) => {
					toastAPI.error({
						message:
							'Error, while deleting draft Investment Programme. Please, try again later.',
					});
					logger.error(error);
				},
			}
		);
	};

	const manageMIPsColumns = () => {
		return [
			{
				id: 'manageDeal',
				size: 70,
				cell: (info) => {
					return (
						<Link
							to={`${GODMODE_ROOT_ROUTES.MANAGE_MIP}/deal/${info.row.original.dealId}`}
						>
							<button className="button is-link">Manage deal</button>
						</Link>
					);
				},
			},
			{
				id: 'dealName',
				size: 50,
				accessorFn: (row) => row?.project?.name,
				header: () => <span className="support-app-compact-text">Deal name</span>,
				cell: (info) => <span className="support-app-compact-text">{info.getValue()}</span>,
			},
			{
				id: 'projectTarget',
				size: 75,
				accessorFn: (row) => row?.project?.targetName,
				header: () => <span className="support-app-compact-text">Deal target name</span>,
				cell: (info) => <span className="support-app-compact-text">{info.getValue()}</span>,
			},
			{
				id: 'mipId',
				size: 75,
				accessorFn: (row) => row.id,
				header: () => (
					<span className="support-app-compact-text">Investment programee Id</span>
				),
				cell: (info) => <span className="support-app-compact-text">{info.getValue()}</span>,
			},
			{
				id: 'entityName',
				size: 50,
				accessorFn: (row) => row.entity?.displayName,
				header: () => <span className="support-app-compact-text">Entity name</span>,
				cell: (info) => <span className="support-app-compact-text">{info.getValue()}</span>,
			},
			{
				id: 'programmeName',
				size: 100,
				accessorFn: (row) => row.programmeName,
				header: () => <span className="support-app-compact-text">Programme name</span>,
				cell: (info) => <span className="support-app-compact-text">{info.getValue()}</span>,
			},
			{
				id: 'creationDate',
				size: 100,
				accessorFn: (row) =>
					formatDateToUI(new Date(row.createdAt), DATE_FORMATS.FULL_DATE_TIME),
				header: () => <span className="support-app-compact-text">Creation date</span>,
				cell: (info) => <span className="support-app-compact-text">{info.getValue()}</span>,
			},
			{
				id: 'deleted',
				size: 50,
				accessorFn: (row) => row.deleted,
				header: () => <span className="support-app-compact-text">is Archived</span>,
				cell: (info) => <SupportAppBooleanStatus value={info.getValue() as boolean} />,
			},
			{
				id: 'is draft ip',
				size: 30,
				accessorFn: (row) => (row.state === DRAFT_STATE ? 'Yes' : 'No'),
				header: () => <span className="support-app-compact-text">Draft IP</span>,
				cell: (info) => <span className="support-app-compact-text">{info.getValue()}</span>,
			},
			{
				id: 'manage',
				size: 50,
				cell: (info) => {
					if (info.row.original.state === DRAFT_STATE) {
						return (
							<Button
								variant="filled"
								color="red"
								onClick={() =>
									setDeleteDraftMipConfirmationModal({
										visible: true,
										mipId: info.row.original.id,
									})
								}
							>
								Delete Draft IP
							</Button>
						);
					}

					return (
						<Link to={`${GODMODE_ROOT_ROUTES.MANAGE_MIP}/mip/${info.row.original.id}`}>
							<button className="button is-link">Manage IP</button>
						</Link>
					);
				},
			},
		];
	};

	const SupportAppBooleanStatus = ({ value }) => <span>{value ? 'Yes' : 'No'}</span>;

	const tanstackTable = useReactTable({
		data: getMIPRequest.data?.payload ?? EMPTY_DATA,
		columns: manageMIPsColumns(),
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		state: {
			sorting,
		},
	});
	const tableContainerRef = useRef<HTMLDivElement>(null);
	const { rows } = tanstackTable.getRowModel();
	const rowVirtualizer = useVirtualizer({
		getScrollElement: () => tableContainerRef.current,
		count: rows.length,
		estimateSize: () => 50,
		overscan: 10,
	});

	return {
		getMIPRequest,
		rows,
		tableContainerRef,
		rowVirtualizer,
		tanstackTable,
		manageSearchQueryForm,
		manageSelectForm,
		deleteDraftMipConfirmationModal,
		setDeleteDraftMipConfirmationModal,
		deleteDraftIPOnSubmit,
		isDeletingDraftIp: deleteDraftIPRequest.isPending,
	};
};
